import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSessions, createSession, deleteSession, getLevels } from '../utils/api';
import { useToast } from './ToastProvider';
import Icon from './Icon';
import '../styles/SessionManager.css';

function SessionManager() {
  const [sessions, setSessions] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    location: '',
    description: '',
    logo: '',
    divers: [],
    instructors: [],
    nonDivers: [],
    guidePalanquiers: []
  });
  const [newDiver, setNewDiver] = useState({ name: '', level: '' });
  const [newInstructor, setNewInstructor] = useState({ name: '', level: '' });
  const [newNonDiver, setNewNonDiver] = useState({ name: '', role: '' });
  const [newGuidePalanquier, setNewGuidePalanquier] = useState({ name: '', role: '' });
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const [sessionsData, levelsData] = await Promise.all([
        getSessions(),
        getLevels()
      ]);
      setSessions(sessionsData);
      setLevels(levelsData);
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createSession({
        ...formData,
        schedule: generateDefaultSchedule(formData.startDate, formData.endDate)
      });
      setShowModal(false);
      resetForm();
      loadSessions();
      toast.success('Session créée avec succès!');
    } catch (error) {
      console.error('Erreur lors de la création de la session:', error);
      toast.error('Erreur lors de la création de la session');
    }
  };

  const generateDefaultSchedule = (startDate, endDate) => {
    const schedule = {};
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateKey = d.toISOString().split('T')[0];
      schedule[dateKey] = {
        activities: [],
        attendance: { divers: {}, instructors: {}, nonDivers: {} },
        notes: ''
      };
    }
    
    return schedule;
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette session ?')) {
      try {
        await deleteSession(id);
        loadSessions();
        toast.success('Session supprimée avec succès!');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression de la session');
      }
    }
  };

  const handleEdit = (session) => {
    setEditingSession(session);
    setFormData({
      name: session.name,
      startDate: session.startDate,
      endDate: session.endDate,
      location: session.location,
      description: session.description || '',
      logo: session.logo || '',
      divers: session.divers || [],
      instructors: session.instructors || [],
      nonDivers: session.nonDivers || [],
      guidePalanquiers: session.guidePalanquiers || []
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const { updateSession } = await import('../utils/api');

      // Mettre à jour le schedule : préserver les jours existants, ajouter les nouveaux
      const newSchedule = generateDefaultSchedule(formData.startDate, formData.endDate);
      const mergedSchedule = {};
      Object.keys(newSchedule).forEach(dateKey => {
        mergedSchedule[dateKey] = editingSession.schedule?.[dateKey] || newSchedule[dateKey];
      });

      await updateSession(editingSession.id, {
        ...editingSession,
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
        location: formData.location,
        description: formData.description,
        logo: formData.logo,
        divers: formData.divers,
        instructors: formData.instructors,
        nonDivers: formData.nonDivers,
        guidePalanquiers: formData.guidePalanquiers,
        schedule: mergedSchedule,
      });
      setShowEditModal(false);
      setEditingSession(null);
      resetForm();
      loadSessions();
      toast.success('Session modifiée avec succès!');
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      toast.error('Erreur lors de la modification de la session');
    }
  };

  const addDiver = () => {
    if (newDiver.name && newDiver.level) {
      setFormData({
        ...formData,
        divers: [...formData.divers, { id: Date.now().toString(), ...newDiver }]
      });
      setNewDiver({ name: '', level: '' });
    }
  };

  const removeDiver = (id) => {
    setFormData({
      ...formData,
      divers: formData.divers.filter(d => d.id !== id)
    });
  };

  const addInstructor = () => {
    if (newInstructor.name && newInstructor.level) {
      setFormData({
        ...formData,
        instructors: [...formData.instructors, { id: Date.now().toString(), ...newInstructor }]
      });
      setNewInstructor({ name: '', level: '' });
    }
  };

  const removeInstructor = (id) => {
    setFormData({
      ...formData,
      instructors: formData.instructors.filter(i => i.id !== id)
    });
  };

  const addNonDiver = () => {
    if (newNonDiver.name) {
      setFormData({
        ...formData,
        nonDivers: [...formData.nonDivers, { id: Date.now().toString(), ...newNonDiver }]
      });
      setNewNonDiver({ name: '', role: '' });
    }
  };

  const removeNonDiver = (id) => {
    setFormData({
      ...formData,
      nonDivers: formData.nonDivers.filter(n => n.id !== id)
    });
  };

  const addGuidePalanquier = () => {
    if (newGuidePalanquier.name) {
      setFormData({
        ...formData,
        guidePalanquiers: [...formData.guidePalanquiers, { id: Date.now().toString(), ...newGuidePalanquier }]
      });
      setNewGuidePalanquier({ name: '', role: '' });
    }
  };

  const removeGuidePalanquier = (id) => {
    setFormData({
      ...formData,
      guidePalanquiers: formData.guidePalanquiers.filter(g => g.id !== id)
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      startDate: '',
      endDate: '',
      location: '',
      description: '',
      logo: '',
      divers: [],
      instructors: [],
      nonDivers: [],
      guidePalanquiers: []
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setFormData(prev => ({ ...prev, logo: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSessionStatus = (session) => {
    const now = new Date();
    const start = new Date(session.startDate);
    const end = new Date(session.endDate);
    
    if (start <= now && end >= now) return 'active';
    if (start > now) return 'upcoming';
    return 'completed';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des sessions...</p>
      </div>
    );
  }

  return (
    <div className="session-manager">
      <div className="page-header">
        <h1>Gestion des Sessions</h1>
        <button onClick={() => setShowModal(true)} className="btn-primary">
          <i className="fas fa-plus"></i> Nouvelle Session
        </button>
      </div>

      <div className="sessions-grid">
        {sessions.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-calendar-alt"></i>
            <h3>Aucune session</h3>
            <p>Créez votre première session de plongée</p>
          </div>
        ) : (
          sessions.map((session) => (
            <div key={session.id} className={`session-card ${getSessionStatus(session)}`}>
              <div className="session-header">
                <h3>{session.name}</h3>
                <span className={`status-badge ${getSessionStatus(session)}`}>
                  {getSessionStatus(session) === 'active' && <><i className="fas fa-water"></i> En cours</>}
                  {getSessionStatus(session) === 'upcoming' && <><i className="fas fa-calendar-alt"></i> À venir</>}
                  {getSessionStatus(session) === 'completed' && <><i className="fas fa-check"></i> Terminée</>}
                </span>
              </div>
              
              <div className="session-info">
                <p><strong><i className="fas fa-map-marker-alt"></i> Lieu:</strong> {session.location}</p>
                <p><strong><i className="fas fa-calendar-alt"></i> Du:</strong> {formatDate(session.startDate)}</p>
                <p><strong><i className="fas fa-calendar-alt"></i> Au:</strong> {formatDate(session.endDate)}</p>
                <p><strong><i className="fas fa-user"></i> Plongeurs:</strong> {session.divers?.length || 0}</p>
                <p><strong><i className="fas fa-user"></i> Moniteurs:</strong> {session.instructors?.length || 0}</p>
                {(session.nonDivers?.length > 0) && (
                  <p><strong><i className="fas fa-user"></i> Non Plongeurs:</strong> {session.nonDivers.length}</p>
                )}
                {(session.guidePalanquiers?.length > 0) && (
                  <p><strong><i className="fas fa-user"></i> Guides Palanquier:</strong> {session.guidePalanquiers.length}</p>
                )}
              </div>

              {session.description && (
                <p className="session-description">{session.description}</p>
              )}

              {(session.logo || true) && (
                <div className="session-logo-wrap">
                  <img
                    src={session.logo || '/asterina.png'}
                    alt="Logo session"
                    className="session-card-logo"
                  />
                </div>
              )}

              <div className="session-actions">
                <button 
                  onClick={() => navigate(`/sessions/${session.id}`)}
                  className="btn-view"
                >
                  <Icon name="eye" /> Voir le planning
                </button>
                <button 
                  onClick={() => handleEdit(session)}
                  className="btn-edit"
                >
                  <Icon name="edit" /> Modifier
                </button>
                <button 
                  onClick={() => handleDelete(session.id)}
                  className="btn-delete"
                >
                  <Icon name="trash" /> Supprimer
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouvelle Session de Plongée</h2>
              <button onClick={() => setShowModal(false)} className="close-btn"><i className="fas fa-times"></i></button>
            </div>

            <form onSubmit={handleSubmit} className="session-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nom de la session *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Ex: Stage Niveau 2 - Hiver 2026"
                  />
                </div>

                <div className="form-group">
                  <label>Lieu *</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    placeholder="Ex: Piscine municipale"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date de début *</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Date de fin *</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    min={formData.startDate}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  placeholder="Description de la session..."
                />
              </div>

              <div className="form-group">
                <label>Logo de la session <span className="label-hint">(affiché en haut à droite sur les fiches PDF)</span></label>
                <div className="logo-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    id="logo-upload-create"
                    className="logo-input"
                    onChange={handleLogoChange}
                  />
                  <label htmlFor="logo-upload-create" className="logo-upload-btn">
                    <Icon name="plus" /> Choisir une image
                  </label>
                  {formData.logo && (
                    <div className="logo-preview-wrap">
                      <img src={formData.logo} alt="Logo session" className="logo-preview" />
                      <button type="button" className="logo-remove" onClick={() => setFormData(p => ({ ...p, logo: '' }))}>
                        <Icon name="close" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Plongeurs</label>
                <div className="add-item-container">
                  <input
                    type="text"
                    value={newDiver.name}
                    onChange={(e) => setNewDiver({ ...newDiver, name: e.target.value })}
                    placeholder="Nom du plongeur"
                  />
                  <select
                    value={newDiver.level}
                    onChange={(e) => setNewDiver({ ...newDiver, level: e.target.value })}
                    className="level-select"
                  >
                    <option value="">Sélectionnez un niveau</option>
                    {levels.map((level) => (
                      <option key={level.id} value={level.name}>
                        {level.name}
                      </option>
                    ))}
                  </select>
                  <button type="button" onClick={addDiver} className="btn-add"><Icon name="plus" /></button>
                </div>
                <div className="items-list">
                  {formData.divers.map((diver) => (
                    <div key={diver.id} className="item-tag">
                      {diver.name} - {diver.level}
                      <button type="button" onClick={() => removeDiver(diver.id)}><Icon name="close" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Moniteurs</label>
                <div className="add-item-container">
                  <input
                    type="text"
                    value={newInstructor.name}
                    onChange={(e) => setNewInstructor({ ...newInstructor, name: e.target.value })}
                    placeholder="Nom du moniteur"
                  />
                  <select
                    value={newInstructor.level}
                    onChange={(e) => setNewInstructor({ ...newInstructor, level: e.target.value })}
                    className="level-select"
                  >
                    <option value="">Sélectionnez un niveau</option>
                    {levels.map((level) => (
                      <option key={level.id} value={level.name}>
                        {level.name}
                      </option>
                    ))}
                  </select>
                  <button type="button" onClick={addInstructor} className="btn-add"><Icon name="plus" /></button>
                </div>
                <div className="items-list">
                  {formData.instructors.map((instructor) => (
                    <div key={instructor.id} className="item-tag">
                      {instructor.name} - {instructor.level}
                      <button type="button" onClick={() => removeInstructor(instructor.id)}><Icon name="close" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Non Plongeurs <span className="label-hint">(accompagnateurs, staff, famille...)</span></label>
                <div className="add-item-container">
                  <input
                    type="text"
                    value={newNonDiver.name}
                    onChange={(e) => setNewNonDiver({ ...newNonDiver, name: e.target.value })}
                    placeholder="Nom"
                  />
                  <input
                    type="text"
                    value={newNonDiver.role}
                    onChange={(e) => setNewNonDiver({ ...newNonDiver, role: e.target.value })}
                    placeholder="Rôle (optionnel)"
                    style={{ maxWidth: '160px' }}
                  />
                  <button type="button" onClick={addNonDiver} className="btn-add"><Icon name="plus" /></button>
                </div>
                <div className="items-list">
                  {formData.nonDivers.map((p) => (
                    <div key={p.id} className="item-tag item-tag-nonplongeur">
                      {p.name}{p.role ? ` — ${p.role}` : ''}
                      <button type="button" onClick={() => removeNonDiver(p.id)}><Icon name="close" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Guides de Palanquier <span className="label-hint">(GP autonomes, plongeurs encadrants...)</span></label>
                <div className="add-item-container">
                  <input
                    type="text"
                    value={newGuidePalanquier.name}
                    onChange={(e) => setNewGuidePalanquier({ ...newGuidePalanquier, name: e.target.value })}
                    placeholder="Nom"
                  />
                  <input
                    type="text"
                    value={newGuidePalanquier.role}
                    onChange={(e) => setNewGuidePalanquier({ ...newGuidePalanquier, role: e.target.value })}
                    placeholder="Rôle (optionnel)"
                    style={{ maxWidth: '160px' }}
                  />
                  <button type="button" onClick={addGuidePalanquier} className="btn-add"><Icon name="plus" /></button>
                </div>
                <div className="items-list">
                  {formData.guidePalanquiers.map((g) => (
                    <div key={g.id} className="item-tag item-tag-guide">
                      {g.name}{g.role ? ` — ${g.role}` : ''}
                      <button type="button" onClick={() => removeGuidePalanquier(g.id)}><Icon name="close" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-cancel">
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Créer la session
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition */}
      {showEditModal && editingSession && (
        <div className="modal-overlay" onClick={() => { setShowEditModal(false); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Modifier la Session</h2>
              <button onClick={() => { setShowEditModal(false); resetForm(); }} className="close-btn"><Icon name="close" /></button>
            </div>

            <form onSubmit={handleEditSubmit} className="session-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nom de la session *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Lieu *</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date de début *</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date de fin *</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    min={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Logo de la session <span className="label-hint">(affiché en haut à droite sur les fiches PDF)</span></label>
                <div className="logo-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    id="logo-upload-edit"
                    className="logo-input"
                    onChange={handleLogoChange}
                  />
                  <label htmlFor="logo-upload-edit" className="logo-upload-btn">
                    <Icon name="plus" /> {formData.logo ? 'Changer le logo' : 'Choisir une image'}
                  </label>
                  {formData.logo && (
                    <div className="logo-preview-wrap">
                      <img src={formData.logo} alt="Logo session" className="logo-preview" />
                      <button type="button" className="logo-remove" onClick={() => setFormData(p => ({ ...p, logo: '' }))}>
                        <Icon name="close" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Plongeurs</label>
                <div className="add-item-container">
                  <input
                    type="text"
                    value={newDiver.name}
                    onChange={(e) => setNewDiver({ ...newDiver, name: e.target.value })}
                    placeholder="Nom du plongeur"
                  />
                  <select
                    value={newDiver.level}
                    onChange={(e) => setNewDiver({ ...newDiver, level: e.target.value })}
                    className="level-select"
                  >
                    <option value="">Sélectionnez un niveau</option>
                    {levels.map((level) => (
                      <option key={level.id} value={level.name}>
                        {level.name}
                      </option>
                    ))}
                  </select>
                  <button type="button" onClick={addDiver} className="btn-add"><Icon name="plus" /></button>
                </div>
                <div className="items-list">
                  {formData.divers.map((diver) => (
                    <div key={diver.id} className="item-tag">
                      {diver.name} - {diver.level}
                      <button type="button" onClick={() => removeDiver(diver.id)}><Icon name="close" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Moniteurs</label>
                <div className="add-item-container">
                  <input
                    type="text"
                    value={newInstructor.name}
                    onChange={(e) => setNewInstructor({ ...newInstructor, name: e.target.value })}
                    placeholder="Nom du moniteur"
                  />
                  <select
                    value={newInstructor.level}
                    onChange={(e) => setNewInstructor({ ...newInstructor, level: e.target.value })}
                    className="level-select"
                  >
                    <option value="">Sélectionnez un niveau</option>
                    {levels.map((level) => (
                      <option key={level.id} value={level.name}>
                        {level.name}
                      </option>
                    ))}
                  </select>
                  <button type="button" onClick={addInstructor} className="btn-add"><Icon name="plus" /></button>
                </div>
                <div className="items-list">
                  {formData.instructors.map((instructor) => (
                    <div key={instructor.id} className="item-tag">
                      {instructor.name} - {instructor.level}
                      <button type="button" onClick={() => removeInstructor(instructor.id)}><Icon name="close" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Non Plongeurs <span className="label-hint">(accompagnateurs, staff, famille...)</span></label>
                <div className="add-item-container">
                  <input
                    type="text"
                    value={newNonDiver.name}
                    onChange={(e) => setNewNonDiver({ ...newNonDiver, name: e.target.value })}
                    placeholder="Nom"
                  />
                  <input
                    type="text"
                    value={newNonDiver.role}
                    onChange={(e) => setNewNonDiver({ ...newNonDiver, role: e.target.value })}
                    placeholder="Rôle (optionnel)"
                    style={{ maxWidth: '160px' }}
                  />
                  <button type="button" onClick={addNonDiver} className="btn-add"><Icon name="plus" /></button>
                </div>
                <div className="items-list">
                  {formData.nonDivers.map((p) => (
                    <div key={p.id} className="item-tag item-tag-nonplongeur">
                      {p.name}{p.role ? ` — ${p.role}` : ''}
                      <button type="button" onClick={() => removeNonDiver(p.id)}><Icon name="close" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Guides de Palanquier <span className="label-hint">(GP autonomes, plongeurs encadrants...)</span></label>
                <div className="add-item-container">
                  <input
                    type="text"
                    value={newGuidePalanquier.name}
                    onChange={(e) => setNewGuidePalanquier({ ...newGuidePalanquier, name: e.target.value })}
                    placeholder="Nom"
                  />
                  <input
                    type="text"
                    value={newGuidePalanquier.role}
                    onChange={(e) => setNewGuidePalanquier({ ...newGuidePalanquier, role: e.target.value })}
                    placeholder="Rôle (optionnel)"
                    style={{ maxWidth: '160px' }}
                  />
                  <button type="button" onClick={addGuidePalanquier} className="btn-add"><Icon name="plus" /></button>
                </div>
                <div className="items-list">
                  {formData.guidePalanquiers.map((g) => (
                    <div key={g.id} className="item-tag item-tag-guide">
                      {g.name}{g.role ? ` — ${g.role}` : ''}
                      <button type="button" onClick={() => removeGuidePalanquier(g.id)}><Icon name="close" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => { setShowEditModal(false); resetForm(); }} className="btn-cancel">
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Enregistrer les modifications
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SessionManager;
